import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Card, CardContent, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  CircularProgress, Alert, IconButton, Tooltip, FormControl, InputLabel,
  Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme,
  Stack, Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TrendingUp as EarnedIcon,
  TrendingDown as OwedIcon, Balance as BalanceIcon,
  Refresh as RefreshIcon, Build as InstallIcon,
  Receipt as ExpenseIcon, LocalGasStation as FuelIcon,
  Payments as DeliverIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import PeriodoDesdeEntrega from '../../components/technician/PeriodoDesdeEntrega';

const txTypeLabels: Record<string, { label: string; color: any }> = {
  cash_collection: { label: 'Cobré del cliente', color: 'warning' },
  cash_delivery: { label: 'Entregué efectivo', color: 'success' },
  commission_earned: { label: 'Comisión ganada', color: 'info' },
  commission_payment: { label: 'Me pagaron comisión', color: 'success' },
  expense: { label: 'Gasto', color: 'error' },
  adjustment: { label: 'Ajuste', color: 'default' },
};

// Estado de una entrega declarada. Los demás movimientos nacen confirmados y no muestran chip.
const estadoLabels: Record<string, { label: string; color: any }> = {
  pending: { label: 'Esperando aprobación', color: 'warning' },
  rejected: { label: 'No aprobada', color: 'error' },
};

// Lo que el técnico gasta en la calle. Se dejan pocas y con nombre corto para que
// anote de una, con el celular en la mano y sin pensarlo mucho.
const CATEGORIAS_GASTO: { value: string; label: string }[] = [
  { value: 'combustible', label: 'Combustible' },
  { value: 'peajes', label: 'Peajes' },
  { value: 'dieta', label: 'Comida' },
  { value: 'materiales', label: 'Materiales' },
  { value: 'repuestos', label: 'Repuestos' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'otros', label: 'Otro' },
];

// Montos que se repiten, para no tener que teclear.
const MONTOS_RAPIDOS = [200, 500, 1000, 2000];

// Con hora: cuando se discute "eso lo instalé después de entregarte", el día solo no basta.
function formatDateTime(d: string) {
  return new Date(d).toLocaleString('es-DO', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// Valor para <input type="date">, en hora local (toISOString lo correría al día anterior
// en República Dominicana, que va en UTC-4).
function inputDate(d: Date) {
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}



function formatCurrency(n: number) {
  return (n ?? 0).toLocaleString('es-DO', { minimumFractionDigits: 0 });
}

const TechnicianMyFinances: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiService.getMyStats();
      setStats(res.data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (typeFilter) params.type = typeFilter;
      const res = await apiService.getMyTransactions(params);
      setTransactions(res.data?.transactions || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (e) {
      console.error('Error fetching transactions:', e);
    } finally {
      setTxLoading(false);
    }
  }, [page, rowsPerPage, typeFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStats(), fetchTransactions()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [expenseDialog, setExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: 'combustible', amount: '', description: '' });
  const [savingExpense, setSavingExpense] = useState(false);
  const [myExpenses, setMyExpenses] = useState<any>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  const [periodo, setPeriodo] = useState<any>(null);
  const [deliveryDialog, setDeliveryDialog] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({ amount: '', deliveredToName: '', date: '', notes: '', deliveryMethod: 'cash' as 'cash' | 'bank_transfer' });
  const [savingDelivery, setSavingDelivery] = useState(false);

  const fetchPeriodo = useCallback(async () => {
    try {
      const res: any = await apiService.getMyPeriod();
      setPeriodo(res.data);
    } catch (e) {
      console.error('Error fetching period:', e);
    }
  }, []);

  useEffect(() => { fetchPeriodo(); }, [fetchPeriodo]);

  const fetchMyExpenses = useCallback(async () => {
    try {
      const res: any = await apiService.getMyExpenses();
      setMyExpenses(res.data);
    } catch (e) {
      console.error('Error fetching my expenses:', e);
    }
  }, []);

  useEffect(() => { fetchMyExpenses(); }, [fetchMyExpenses]);

  const handleSaveExpense = async () => {
    const monto = Number(expenseForm.amount);
    if (!Number.isFinite(monto) || monto <= 0) {
      setSnack({ open: true, msg: 'Escribe un monto mayor a 0', sev: 'error' });
      return;
    }
    setSavingExpense(true);
    try {
      const res: any = await apiService.createMyExpense({
        category: expenseForm.category,
        amount: monto,
        description: expenseForm.description,
      });
      setExpenseDialog(false);
      setExpenseForm({ category: 'combustible', amount: '', description: '' });
      setSnack({ open: true, msg: res?.message || 'Gasto registrado', sev: 'success' });
      fetchStats(); fetchTransactions(); fetchMyExpenses(); fetchPeriodo();
    } catch (e: any) {
      setSnack({ open: true, msg: e?.response?.data?.message || 'No se pudo registrar el gasto', sev: 'error' });
    } finally {
      setSavingExpense(false);
    }
  };

  const abrirEntrega = () => {
    // Se propone el NETO — lo que de verdad cambia de mano — y la fecha de hoy. El técnico
    // se cobra su comisión de este mismo dinero; no entrega el bruto para que después se la
    // devuelvan. El servidor reconstruye las dos patas a partir de lo que entregó.
    const efectivo = stats?.balance?.cashOwedToCompany || 0;
    const comision = stats?.balance?.commissionsOwedByCompany || 0;
    setDeliveryForm({
      amount: String(Math.max(0, efectivo - comision)),
      deliveredToName: '',
      date: inputDate(new Date()),
      notes: '',
      deliveryMethod: 'cash',
    });
    setDeliveryDialog(true);
  };

  const handleSaveDelivery = async () => {
    const monto = Number(deliveryForm.amount);
    if (!Number.isFinite(monto) || monto <= 0) {
      setSnack({ open: true, msg: 'Escribe un monto mayor a 0', sev: 'error' });
      return;
    }
    if (!deliveryForm.deliveredToName.trim()) {
      setSnack({ open: true, msg: '¿A quién le entregaste el dinero?', sev: 'error' });
      return;
    }
    setSavingDelivery(true);
    try {
      const res: any = await apiService.declareMyCashDelivery({
        amount: monto,
        deliveredToName: deliveryForm.deliveredToName.trim(),
        date: deliveryForm.date ? new Date(`${deliveryForm.date}T12:00:00`).toISOString() : undefined,
        notes: deliveryForm.notes,
        deliveryMethod: deliveryForm.deliveryMethod,
        deductCommission: true,
      });
      setDeliveryDialog(false);
      setSnack({ open: true, msg: res?.message || 'Entrega registrada', sev: 'success' });
      fetchStats(); fetchTransactions(); fetchPeriodo();
    } catch (e: any) {
      setSnack({ open: true, msg: e?.response?.data?.message || 'No se pudo registrar la entrega', sev: 'error' });
    } finally {
      setSavingDelivery(false);
    }
  };

  const handleRefresh = () => { fetchStats(); fetchTransactions(); fetchMyExpenses(); fetchPeriodo(); };

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const balance = stats?.balance || { cashOwedToCompany: 0, commissionsOwedByCompany: 0, netBalance: 0 };

  // El técnico no lee un balance con signo: lee "cuánto entrego" o "cuánto me van a pagar".
  // Al liquidar se registran los dos movimientos completos (entrega el efectivo bruto y
  // recibe la comisión), así que la diferencia es lo que cambia de mano de verdad.
  const debeEntregar = Math.max(0, balance.cashOwedToCompany - balance.commissionsOwedByCompany);
  const leDeben = Math.max(0, balance.commissionsOwedByCompany - balance.cashOwedToCompany);

  // Gastos ya descontados del efectivo desde la última entrega. Se nombran aparte porque
  // si no, el técnico ve menos efectivo del que cobró y no sabe a dónde se fue.
  const gastos = stats?.gastosDescontados || { total: 0, count: 0 };
  const month = stats?.thisMonth || {};

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.6rem', sm: '2.125rem' } }}>Mis Finanzas</Typography>
          <Typography variant="body2" color="text.secondary">Balance y transacciones</Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={handleRefresh} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Anotar un gasto. Va arriba y grande a propósito: es lo que el técnico hace
          en la calle, con una mano y de prisa. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <Button
          fullWidth size="large" variant="contained" color="error"
          startIcon={<FuelIcon />}
          onClick={() => setExpenseDialog(true)}
          sx={{ py: 1.5, fontWeight: 700 }}
        >
          Anotar un gasto
        </Button>
        <Button
          fullWidth size="large" variant="contained" color="success"
          startIcon={<DeliverIcon />}
          onClick={abrirEntrega}
          disabled={!(balance.cashOwedToCompany > 0) || !!periodo?.entregaPendiente}
          sx={{ py: 1.5, fontWeight: 700 }}
        >
          Entregué efectivo
        </Button>
      </Stack>

      {myExpenses?.thisMonth?.count > 0 && (
        <Alert severity="info" icon={<ExpenseIcon />} sx={{ mb: 2 }}>
          Llevas <strong>RD${formatCurrency(myExpenses.thisMonth.total)}</strong> en gastos este mes
          ({myExpenses.thisMonth.count} {myExpenses.thisMonth.count === 1 ? 'registro' : 'registros'}).
        </Alert>
      )}

      {/* Balance Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ backgroundColor: 'info.main', color: 'white', borderRadius: 2, p: 1.5, display: 'flex' }}>
                <EarnedIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600}>{formatCurrency(balance.commissionsOwedByCompany)}</Typography>
                <Typography variant="body2" color="text.secondary">Driports me debe (comisiones)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ backgroundColor: 'warning.main', color: 'white', borderRadius: 2, p: 1.5, display: 'flex' }}>
                <OwedIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600}>{formatCurrency(balance.cashOwedToCompany)}</Typography>
                <Typography variant="body2" color="text.secondary">Le debo a Driports (efectivo)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ backgroundColor: debeEntregar > 0 ? 'error.main' : 'success.main', color: 'white', borderRadius: 2, p: 1.5, display: 'flex' }}>
                <BalanceIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600} color={debeEntregar > 0 ? 'error.main' : 'success.main'}>
                  {formatCurrency(debeEntregar > 0 ? debeEntregar : leDeben)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {debeEntregar > 0 ? 'Debo entregar a Driports' : leDeben > 0 ? 'Driports me va a pagar' : 'Estoy al día'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ backgroundColor: 'primary.main', color: 'white', borderRadius: 2, p: 1.5, display: 'flex' }}>
                <InstallIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600}>{month.installations || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Instalaciones (Mes)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Explanation */}
      <Alert severity={debeEntregar > 0 ? 'warning' : 'info'} sx={{ mb: 3 }}>
        {debeEntregar > 0 && (
          <>
            Tienes <strong>RD${formatCurrency(balance.cashOwedToCompany)}</strong> en efectivo de clientes
            por entregar
            {gastos.total > 0 && (
              <>
                {' '}(ya se te descontaron <strong>RD${formatCurrency(gastos.total)}</strong> de{' '}
                {gastos.count} {gastos.count === 1 ? 'gasto que anotaste' : 'gastos que anotaste'})
              </>
            )}
            {' '}y Driports te debe <strong>RD${formatCurrency(balance.commissionsOwedByCompany)}</strong> en comisiones.
            Al liquidar entregas el efectivo y recibes tus comisiones: de tu mano salen{' '}
            <strong>RD${formatCurrency(debeEntregar)}</strong>.
          </>
        )}
        {leDeben > 0 && (
          <>
            No tienes efectivo pendiente de entregar y Driports te debe{' '}
            <strong>RD${formatCurrency(leDeben)}</strong>.
            {gastos.total > 0 && ' Ahí va incluido lo que pusiste de tu bolsillo en los gastos que anotaste.'}
          </>
        )}
        {debeEntregar === 0 && leDeben === 0 && 'Estás al día: no debes efectivo y no hay comisiones pendientes.'}
      </Alert>

      {/* Mismo componente que ve el admin: si no cuadran las cuentas, miran la misma pantalla. */}
      <PeriodoDesdeEntrega periodo={periodo} enPrimeraPersona />

      {/* Transactions */}
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Mis movimientos</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={typeFilter} label="Tipo" onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(txTypeLabels).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          {txLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <>
              {/* En el celular una tabla de 5 columnas se sale de la pantalla; ahí van tarjetas. */}
              {isMobile ? (
                <Stack spacing={1} sx={{ px: 1.5, pb: 1 }}>
                  {transactions.map((tx: any) => {
                    const cfg = txTypeLabels[tx.transactionType] || { label: tx.transactionType, color: 'default' };
                    const est = estadoLabels[tx.status];
                    return (
                      <Box key={tx._id} sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Chip label={cfg.label} color={cfg.color} size="small" sx={{ mb: 0.5 }} />
                            {est && <Chip label={est.label} color={est.color} size="small" sx={{ ml: 0.5, mb: 0.5 }} />}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatDateTime(tx.transactionDate || tx.createdAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body1" fontWeight={700} sx={{ flexShrink: 0 }}>
                            RD${formatCurrency(tx.amount)}
                          </Typography>
                        </Box>
                        {(tx.relatedDocuments?.clientId?.name || tx.deliveredToName || tx.notes) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {tx.relatedDocuments?.clientId?.name
                              || (tx.deliveredToName ? `Entregado a ${tx.deliveredToName}` : '')}
                            {tx.notes ? `${tx.relatedDocuments?.clientId?.name || tx.deliveredToName ? ' — ' : ''}${tx.notes}` : ''}
                          </Typography>
                        )}
                        {tx.status === 'rejected' && tx.rejectedReason && (
                          <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                            Motivo: {tx.rejectedReason}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                  {transactions.length === 0 && (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No hay movimientos</Typography>
                  )}
                </Stack>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Movimiento</TableCell>
                      <TableCell align="right">Monto (DOP)</TableCell>
                      <TableCell>Cliente / entregado a</TableCell>
                      <TableCell>Notas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((tx: any) => {
                      const cfg = txTypeLabels[tx.transactionType] || { label: tx.transactionType, color: 'default' };
                      const est = estadoLabels[tx.status];
                      return (
                        <TableRow key={tx._id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDateTime(tx.transactionDate || tx.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Chip label={cfg.label} color={cfg.color} size="small" />
                            {est && <Chip label={est.label} color={est.color} size="small" sx={{ ml: 0.5 }} />}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(tx.amount)}
                          </TableCell>
                          <TableCell>
                            {tx.relatedDocuments?.clientId?.name
                              || (tx.deliveredToName ? `→ ${tx.deliveredToName}` : '-')}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                              {tx.status === 'rejected' && tx.rejectedReason ? tx.rejectedReason : (tx.notes || '-')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {transactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No hay movimientos</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
              <TablePagination
                component="div" count={total} page={page} rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Filas por página"
              />
            </>
          )}
        </TableContainer>
      </Paper>

      {/* Entregué efectivo — el técnico deja constancia de SU fecha y de a quién se lo dio.
          Baja del balance solo cuando el admin la aprueba. */}
      <Dialog open={deliveryDialog} onClose={() => setDeliveryDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeliverIcon color="success" /> Entregué efectivo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pon lo que entregaste EN MANO, ya con tu comisión descontada. Queda anotado con la
            fecha que pongas y a quién se lo diste, y baja de tu efectivo cuando lo aprueben.
          </Typography>

          {balance.commissionsOwedByCompany > 0 && (
            <Alert severity="info" icon={false} sx={{ mb: 2, py: 1 }}>
              <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Efectivo que tienes</span><strong>{formatCurrency(balance.cashOwedToCompany)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Menos tu comisión</span><strong>− {formatCurrency(balance.commissionsOwedByCompany)}</strong>
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>Entregas</span><span>{formatCurrency(debeEntregar)}</span>
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth type="number" label="¿Cuánto entregaste en mano? (DOP)"
            value={deliveryForm.amount}
            onChange={e => setDeliveryForm({ ...deliveryForm, amount: e.target.value })}
            sx={{ mb: 1, '& input': { fontSize: 16 } }}
            inputProps={{ inputMode: 'numeric' }}
          />
          <Button
            size="small" sx={{ mb: 2 }}
            onClick={() => setDeliveryForm({ ...deliveryForm, amount: String(debeEntregar) })}
          >
            {balance.commissionsOwedByCompany > 0
              ? `Cuadré todo (RD$${formatCurrency(debeEntregar)})`
              : `Entregué todo (RD$${formatCurrency(debeEntregar)})`}
          </Button>

          <Typography variant="caption" sx={{ fontWeight: 600 }}>¿Cómo lo entregaste?</Typography>
          <ToggleButtonGroup
            exclusive fullWidth
            value={deliveryForm.deliveryMethod}
            onChange={(_, v) => { if (v) setDeliveryForm({ ...deliveryForm, deliveryMethod: v }); }}
            sx={{ mt: 0.5, mb: 2, '& .MuiToggleButton-root': { borderRadius: 2, py: 1 } }}
          >
            <ToggleButton value="cash">Efectivo</ToggleButton>
            <ToggleButton value="bank_transfer">Transferencia</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            fullWidth label="¿A quién se lo entregaste?"
            placeholder="Ej: la mamá de Carlos"
            value={deliveryForm.deliveredToName}
            onChange={e => setDeliveryForm({ ...deliveryForm, deliveredToName: e.target.value })}
            sx={{ mb: 2, '& input': { fontSize: 16 } }}
          />

          <TextField
            fullWidth type="date" label="¿Qué día?"
            value={deliveryForm.date}
            onChange={e => setDeliveryForm({ ...deliveryForm, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: inputDate(new Date()) }}
            sx={{ mb: 2, '& input': { fontSize: 16 } }}
          />

          <TextField
            fullWidth label="Nota (opcional)" multiline rows={2}
            value={deliveryForm.notes}
            onChange={e => setDeliveryForm({ ...deliveryForm, notes: e.target.value })}
            sx={{ '& textarea': { fontSize: 16 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeliveryDialog(false)} disabled={savingDelivery}>Cancelar</Button>
          <Button
            variant="contained" color="success" onClick={handleSaveDelivery}
            disabled={savingDelivery || !deliveryForm.amount || !deliveryForm.deliveredToName.trim()}
            startIcon={savingDelivery ? <CircularProgress size={18} /> : <DeliverIcon />}
          >
            {savingDelivery ? 'Guardando...' : 'Registrar entrega'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Anotar gasto — pocos toques: categoría, monto y listo. */}
      <Dialog open={expenseDialog} onClose={() => setExpenseDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FuelIcon color="error" /> Anotar un gasto
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se descuenta del efectivo que tienes en mano. Si no te queda efectivo, la
            empresa te lo debe y te lo devuelve con tus comisiones.
          </Typography>

          <Typography variant="caption" sx={{ fontWeight: 600 }}>¿En qué fue?</Typography>
          <ToggleButtonGroup
            exclusive
            value={expenseForm.category}
            onChange={(_, v) => { if (v) setExpenseForm({ ...expenseForm, category: v }); }}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, mb: 2, '& .MuiToggleButton-root': { borderRadius: 2, border: '1px solid', flex: '1 1 auto' } }}
          >
            {CATEGORIAS_GASTO.map(c => (
              <ToggleButton key={c.value} value={c.value} sx={{ px: 1.5, py: 0.75, textTransform: 'none' }}>
                {c.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography variant="caption" sx={{ fontWeight: 600 }}>¿Cuánto?</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, mb: 1.5 }}>
            {MONTOS_RAPIDOS.map(m => (
              <Button
                key={m}
                size="small"
                variant={Number(expenseForm.amount) === m ? 'contained' : 'outlined'}
                onClick={() => setExpenseForm({ ...expenseForm, amount: String(m) })}
                sx={{ flex: '1 1 auto', minWidth: 70 }}
              >
                {m.toLocaleString('es-DO')}
              </Button>
            ))}
          </Box>

          <TextField
            fullWidth
            type="number"
            label="Monto en RD$"
            value={expenseForm.amount}
            onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
            inputProps={{ inputMode: 'decimal', style: { fontSize: '1.4rem', fontWeight: 700 } }}
            sx={{ mb: 2 }}
            autoFocus
          />

          <TextField
            fullWidth
            label="Nota (opcional)"
            placeholder="Ej: gasolina camino a Santiago"
            value={expenseForm.description}
            onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExpenseDialog(false)} disabled={savingExpense}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            size="large"
            onClick={handleSaveExpense}
            disabled={savingExpense || !expenseForm.amount}
          >
            {savingExpense ? 'Guardando…' : 'Guardar gasto'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechnicianMyFinances;
